// src/pages/projects/NCDHIVIntegration.jsx
import React, { useState, useEffect } from 'react';
import { Heart, Activity, Stethoscope, TrendingUp, Users, MapPin, Clock, Award, Zap, Building, Mail, Phone, Target, CheckCircle } from 'lucide-react';

const NCDHIVIntegration = () => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const projectStats = [
    { number: '5,200+', label: 'Patients Enrolled', icon: Users },
    { number: '78%', label: 'Improved Health Outcomes', icon: TrendingUp },
    { number: '15', label: 'Integrated Health Centers', icon: MapPin },
    { number: '98%', label: 'Treatment Adherence Rate', icon: Award }
  ];

  const integrationAreas = [
    {
      icon: Heart,
      title: 'Cardiovascular Care Integration',
      description: 'Comprehensive management of hypertension and heart disease alongside HIV care for optimal patient outcomes.',
      highlights: ['Blood pressure monitoring', 'Cardiac risk assessment', 'Lifestyle counseling', 'Medication management']
    },
    {
      icon: Activity,
      title: 'Diabetes Management',
      description: 'Integrated diabetes care ensuring HIV patients receive comprehensive metabolic health support.',
      highlights: ['Blood glucose monitoring', 'Nutrition education', 'Exercise programs', 'Insulin management']
    },
    {
      icon: Stethoscope,
      title: 'Integrated Clinical Services',
      description: 'Streamlined healthcare delivery combining NCD and HIV services in a single patient visit.',
      highlights: ['One-stop consultations', 'Unified treatment protocols', 'Shared medical records', 'Coordinated care teams']
    },
    {
      icon: Zap,
      title: 'Health System Strengthening',
      description: 'Building robust healthcare infrastructure capable of delivering integrated chronic disease management.',
      highlights: ['Staff training programs', 'Equipment procurement', 'Data management systems', 'Quality assurance']
    }
  ];

  const keyBenefits = [
    { title: 'Improved Patient Experience', description: 'Single visit for multiple chronic conditions reduces burden on patients' },
    { title: 'Enhanced Efficiency', description: 'Better resource utilization and reduced healthcare delivery costs' },
    { title: 'Better Health Outcomes', description: 'Comprehensive care approach leads to improved management of both conditions' }
  ];

  const ncdConditions = [
    { name: 'Hypertension', prevalence: '35%', icon: Heart },
    { name: 'Diabetes', prevalence: '18%', icon: Activity },
    { name: 'Mental Health', prevalence: '42%', icon: Users },
    { name: 'Obesity', prevalence: '28%', icon: TrendingUp }
  ];

  const specificObjectives = [
    'Raise awareness on HIV and NCD Comorbidities among the population of Rakai/Uganda.',
    'Screen individuals for both HIV and selected NCDs particularly Hypertension, Diabetes and mental Illnesses.',
    'Strengthen partnerships with government and private sector stakeholders to prevent and manage HIV and NCDs.',
    'Provide training to healthcare workers in HIV and NCD management Integration.',
    'Link diagnosed HIV & NCD positive patients to care AND ensure adherence.'
  ];

  const keyActivities = [
    'Community mobilization and awareness on HIV & NCDs',
    'HIV and NCDs screening',
    'Health education and sensitization',
    'Follow-up of defaulters and non-suppressors',
    'Training for healthcare providers',
    'Linkage and referral for HIV and NCD cases',
    'ART and Anti NCD medicines adherence counseling'
  ];

  const achievements = [
    'Conducted 5 community advocacy & sensitisation meetings on HIV &NCDs',
    'Screened 300 people for HIV and NCDs',
    'Diagnosed & linked 15 HIV-positive clients to ART services',
    '6/15 had HIV & NCD co-morbidity',
    'Traced and restarted 30 ART defaulters',
    'Conducted adherence counseling for 10 non-suppressors',
    'Trained 20 healthcare workers in adherence counseling'
  ];

  return (
    <div className="project-page">
      {/* Bootstrap CSS */}
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet" 
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row align-items-center min-vh-70">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">Innovation Project</div>
                <h1 className="hero-title">
                  NCD/HIV Integration Project
                </h1>
                <p className="hero-subtitle">
                  Revolutionizing Chronic Disease Care
                </p>
                <p className="hero-description">
                  Pioneering integrated healthcare delivery that combines Non-Communicable Disease (NCD) 
                  management with HIV care to provide comprehensive, efficient, and patient-centered health services.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-container">
                <div className="project-image">
                  <img 
                    src="/images/projects/hiv.png" 
                    alt="NCD/HIV Integration Project"
                    className="hero-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="image-placeholder">
                    <Stethoscope size={60} />
                    <span>Project Image</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Organizational Profile Section */}
      <section className="profile-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header text-center">
                <div className="section-badge">Organizational Profile</div>
                <h2 className="section-title">Rakai Community Health Initiative (RACOHI)</h2>
                <p className="section-description">
                  Leading the fight against HIV and NCD co-morbidities through innovative integration approaches
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Contact Information */}
            <div className="col-lg-4 mb-4">
              <div className="profile-card">
                <div className="profile-icon">
                  <Building size={32} />
                </div>
                <h4 className="profile-title">Contact Information</h4>
                <div className="profile-details">
                  <div className="detail-item">
                    <MapPin size={16} />
                    <span>Buyamba Trading Centre, Rakai District, Uganda</span>
                  </div>
                  <div className="detail-item">
                    <Mail size={16} />
                    <span>P.O. Box 277, Kyotera – Uganda</span>
                  </div>
                  <div className="detail-item">
                    <Mail size={16} />
                    <span>rakaicommunityhealthinitiative@gmail.com</span>
                  </div>
                  <div className="detail-item">
                    <Phone size={16} />
                    <span>+256 701 816 912 / +256 778 173 081</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Objectives */}
            <div className="col-lg-8 mb-4">
              <div className="profile-card">
                <div className="profile-icon">
                  <Target size={32} />
                </div>
                <h4 className="profile-title">Project Objectives</h4>
                <div className="objective-content">
                  <div className="general-objective">
                    <h5>General Objective</h5>
                    <p>To control HIV and NCD Co-morbidities for the population of Rakai and Uganda at large by 2040.</p>
                  </div>
                  <div className="specific-objectives">
                    <h5>Specific Objectives</h5>
                    <ul className="objectives-list">
                      {specificObjectives.map((objective, index) => (
                        <li key={index}>
                          <CheckCircle size={16} />
                          <span>{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Key Activities */}
            <div className="col-lg-6 mb-4">
              <div className="profile-card">
                <div className="profile-icon">
                  <Activity size={32} />
                </div>
                <h4 className="profile-title">Key Activities</h4>
                <ul className="activities-list">
                  {keyActivities.map((activity, index) => (
                    <li key={index}>
                      <CheckCircle size={16} />
                      <span>{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Achievements */}
            <div className="col-lg-6 mb-4">
              <div className="profile-card">
                <div className="profile-icon">
                  <Award size={32} />
                </div>
                <h4 className="profile-title">Achievements (FY 2024/2025)</h4>
                <ul className="achievements-list">
                  {achievements.map((achievement, index) => (
                    <li key={index}>
                      <CheckCircle size={16} />
                      <span>{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NCD Prevalence Section */}
      <section className="prevalence-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header text-center">
                <div className="section-badge">Health Challenge</div>
                <h2 className="section-title">NCD Prevalence in HIV Patients</h2>
                <p className="section-description">
                  Non-communicable diseases are increasingly common among people living with HIV, 
                  requiring integrated care approaches for optimal health outcomes.
                </p>
              </div>
            </div>
          </div>
          
          <div className="row">
            {ncdConditions.map((condition, index) => (
              <div key={index} className="col-lg-3 col-md-6 mb-4">
                <div className="condition-card">
                  <div className="condition-icon">
                    <condition.icon size={32} />
                  </div>
                  <h4 className="condition-name">{condition.name}</h4>
                  <div className="condition-prevalence">{condition.prevalence}</div>
                  <div className="condition-label">Prevalence Rate</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="overview-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="project-image-container">
                <img 
                  src="/images/projects/ncd-hiv-overview.jpg" 
                  alt="NCD/HIV Integration Overview"
                  className="project-overview-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder">
                  <Activity size={60} />
                  <span>Project Overview</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="overview-content">
                <div className="section-badge">Integrated Approach</div>
                <h2 className="section-title">Comprehensive Care Model</h2>
                <p className="section-description">
                  Our NCD/HIV integration project addresses the growing challenge of managing multiple 
                  chronic conditions in a single healthcare setting. By combining services, we reduce 
                  patient burden while improving health outcomes and healthcare efficiency.
                </p>
                
                <div className="benefits-areas">
                  <h4 className="subsection-title">Key Benefits</h4>
                  {keyBenefits.map((benefit, index) => (
                    <div key={index} className="benefit-item">
                      <h5 className="benefit-title">{benefit.title}</h5>
                      <p className="benefit-description">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Areas */}
      <section className="integration-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className={`section-header text-center ${animateIn ? 'animate-in' : ''}`}>
                <div className="section-badge">Service Integration</div>
                <h2 className="section-title">Integrated Care Components</h2>
                <p className="section-description">
                  Our comprehensive integration model addresses multiple aspects of chronic disease 
                  management within existing HIV care infrastructure.
                </p>
              </div>
            </div>
          </div>
          
          <div className="row">
            {integrationAreas.map((area, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="integration-card">
                  <div className="integration-icon">
                    <area.icon size={32} />
                  </div>
                  <div className="integration-content">
                    <h3 className="integration-title">{area.title}</h3>
                    <p className="integration-description">{area.description}</p>
                    <ul className="integration-highlights">
                      {area.highlights.map((highlight, idx) => (
                        <li key={idx}>
                          <TrendingUp size={16} />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="section-header text-center">
                <div className="section-badge">Project Impact</div>
                <h2 className="section-title">Implementation Timeline</h2>
                <p className="section-description">
                  Track the progressive implementation and impact of our integrated care model.
                </p>
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-12">
              <div className="timeline-container">
                <div className="timeline-item">
                  <div className="timeline-icon">
                    <Clock size={24} />
                  </div>
                  <div className="timeline-content">
                    <h4 className="timeline-title">Phase 1: Foundation</h4>
                    <p className="timeline-description">Infrastructure setup and staff training across 5 health centers</p>
                    <span className="timeline-date">2022 - 2023</span>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="timeline-icon">
                    <Users size={24} />
                  </div>
                  <div className="timeline-content">
                    <h4 className="timeline-title">Phase 2: Expansion</h4>
                    <p className="timeline-description">Scaling to 15 health centers with full service integration</p>
                    <span className="timeline-date">2023 - 2024</span>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="timeline-icon">
                    <Award size={24} />
                  </div>
                  <div className="timeline-content">
                    <h4 className="timeline-title">Phase 3: Optimization</h4>
                    <p className="timeline-description">Quality improvement and sustainability planning</p>
                    <span className="timeline-date">2024 - 2025</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Styling with #1e3a8a Theme */}
      <style jsx>{`
        :root {
          --primary-color: #1e3a8a;
          --primary-dark: #1e40af;
          --primary-light: #3b82f6;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
          padding-top: 80px;
        }

        /* Hero Section - Reduced top spacing */
        .hero-section {
          position: relative;
          min-height: 70vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 30px 0;
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
        }

        .hero-overlay {
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(30,58,138,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(30,58,138,0.03)"/></svg>') repeat;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          padding-right: 2rem;
        }

        .hero-badge {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(30, 58, 138, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hero-title {
          font-size: clamp(2.5rem, 4vw, 4rem);
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 12px;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.5rem;
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 24px;
        }

        .hero-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
          max-width: 600px;
        }

        .hero-image-container {
          position: relative;
          padding-left: 2rem;
        }

        .project-image {
          position: relative;
          width: 100%;
          height: 400px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: fit-cover;
        }

        .image-placeholder {
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          color: white;
          text-align: center;
          gap: 16px;
        }

        /* Stats Section */
        .stats-section {
          padding: 80px 0;
          background: white;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 32px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        .stats-grid.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .stat-card {
          text-align: center;
          padding: 32px 24px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .stat-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 1rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        /* Profile Section */
        .profile-section {
          padding: 100px 0;
          background: var(--light-bg);
        }

        .profile-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid var(--border-light);
        }

        .profile-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .profile-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 24px;
        }

        .profile-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 24px;
        }

        .profile-details {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .detail-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        .detail-item svg {
          color: var(--primary-color);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .general-objective {
          margin-bottom: 32px;
        }

        .general-objective h5 {
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 12px;
        }

        .general-objective p {
          color: var(--text-gray);
          font-size: 1.1rem;
          line-height: 1.6;
        }

        .specific-objectives h5 {
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 16px;
        }

        .objectives-list,
        .activities-list,
        .achievements-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .objectives-list li,
        .activities-list li,
        .achievements-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .objectives-list li svg,
        .activities-list li svg,
        .achievements-list li svg {
          color: var(--success-green);
          flex-shrink: 0;
          margin-top: 2px;
        }

        /* Prevalence Section */
        .prevalence-section {
          padding: 100px 0;
          background: white;
        }

        .condition-card {
          background: white;
          border-radius: 16px;
          padding: 32px 24px;
          text-align: center;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
        }

        .condition-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .condition-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, var(--primary-light), var(--primary-color));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .condition-name {
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .condition-prevalence {
          font-size: 2rem;
          font-weight: 800;
          color: var(--primary-color);
          margin-bottom: 4px;
        }

        .condition-label {
          font-size: 0.9rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        /* Overview Section */
        .overview-section {
          padding: 100px 0;
          background: var(--light-bg);
        }

        .project-image-container {
          position: relative;
        }

        .project-overview-image {
          width: 100%;
          height: 500px;
          object-fit: cover;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .overview-content {
          padding-left: 48px;
        }

        .section-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(30, 58, 138, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .section-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 24px;
          line-height: 1.2;
        }

        .section-description {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .benefits-areas {
          margin-top: 48px;
        }

        .subsection-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 24px;
        }

        .benefit-item {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-light);
        }

        .benefit-item:last-child {
          border-bottom: none;
        }

        .benefit-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--primary-color);
          margin-bottom: 8px;
        }

        .benefit-description {
          color: var(--text-gray);
          margin: 0;
        }

        /* Integration Section */
        .integration-section {
          padding: 100px 0;
          background: white;
        }

        .section-header {
          margin-bottom: 80px;
        }

        .integration-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid var(--border-light);
        }

        .integration-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }

        .integration-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 24px;
        }

        .integration-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
        }

        .integration-description {
          color: var(--text-gray);
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .integration-highlights {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .integration-highlights li {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }

        .integration-highlights li svg {
          color: var(--success-green);
          flex-shrink: 0;
        }

        /* Timeline Section */
        .timeline-section {
          padding: 100px 0;
          background: var(--light-bg);
        }

        .timeline-container {
          position: relative;
          max-width: 800px;
          margin: 0 auto;
        }

        .timeline-container::before {
          content: '';
          position: absolute;
          left: 40px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(to bottom, var(--primary-color), var(--primary-dark));
        }

        .timeline-item {
          position: relative;
          display: flex;
          align-items: flex-start;
          gap: 32px;
          margin-bottom: 60px;
        }

        .timeline-item:last-child {
          margin-bottom: 0;
        }

        .timeline-icon {
          position: relative;
          z-index: 2;
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 8px 24px rgba(30, 58, 138, 0.3);
        }

        .timeline-content {
          flex: 1;
          background: white;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
        }

        .timeline-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 12px;
        }

        .timeline-description {
          color: var(--text-gray);
          margin-bottom: 16px;
          line-height: 1.6;
        }

        .timeline-date {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(30, 58, 138, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        /* CTA Section */
        .cta-section {
          padding: 100px 0;
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
        }

        .cta-card {
          text-align: center;
          padding: 80px 40px;
        }

        .cta-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: white;
          margin-bottom: 24px;
        }

        .cta-description {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 40px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .cta-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Button Styles */
        .btn {
          padding: 16px 32px;
          border-radius: 12px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          border: 2px solid transparent;
          font-size: 1rem;
        }

        .btn-primary {
          background: var(--accent-orange);
          border-color: var(--accent-orange);
          color: white;
        }

        .btn-primary:hover {
          background: #d97706;
          border-color: #d97706;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(245, 158, 11, 0.3);
        }

        .btn-outline-light {
          background: transparent;
          border-color: white;
          color: white;
        }

        .btn-outline-light:hover {
          background: white;
          color: var(--primary-color);
          transform: translateY(-2px);
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .overview-content {
            padding-left: 24px;
          }
          
          .hero-content {
            padding-right: 1rem;
          }
          
          .hero-image-container {
            padding-left: 1rem;
          }
        }

        @media (max-width: 992px) {
          .overview-content {
            padding-left: 0;
            margin-top: 48px;
          }
          
          .hero-content {
            padding-right: 0;
            margin-bottom: 48px;
          }
          
          .hero-image-container {
            padding-left: 0;
          }
        }

        @media (max-width: 768px) {
          .hero-section {
            padding: 20px 0;
          }
          
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 24px;
          }
          
          .integration-card, .profile-card {
            padding: 32px 24px;
          }
          
          .cta-card {
            padding: 60px 24px;
          }
          
          .timeline-container::before {
            left: 20px;
          }
          
          .timeline-icon {
            width: 60px;
            height: 60px;
          }
          
          .btn {
            padding: 14px 28px;
            font-size: 0.95rem;
          }
        }

        /* Animation Utilities */
        .animate-in {
          animation: slideInUp 0.8s ease-out forwards;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default NCDHIVIntegration;